// Formatting and month arithmetic for the Earnings screen. Months are
// "YYYY-MM" strings on the professional's local calendar, same convention as
// the calendar dates elsewhere in the app.

import { ValidationError } from '../../api/client'
import { formatPounds } from '../../plan/wording'
import { formatPrice } from '../../catalog/format'
import { saveErrorMessage } from '../settings/toast'
import type { EarningsLine } from '../../types/app'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function parseMonth(month: string): { year: number; month: number } {
  const [year, monthIndex] = month.split('-').map(Number)
  return { year, month: monthIndex }
}

// parseDate splits "YYYY-MM-DD" (or "YYYY-MM", day undefined) into parts.
function parseDate(date: string): { year: number; month: number; day?: number } {
  const [year, month, day] = date.split('-').map(Number)
  return { year, month, day: Number.isNaN(day) ? undefined : day }
}

// money is real money to the penny (review 2026-10-14): "£12.49", "£1,250",
// "−£120" — so the lines on screen add up to the totals and match the
// accountant's CSV. Whole pounds show no ".00". Negative keeps the Goals tone
// rule: a minus sign, never red.
export const money = formatPrice

// estimate is for projections only (a tax-year forecast, a goal's
// assumption): rounded to the pound, since a penny there would claim a
// precision the guess doesn't have.
export const estimate = formatPounds


// share formats basis points as a rounded percentage, e.g. 2500 -> "25%".
export function share(basisPoints: number): string {
  return `${Math.round(basisPoints / 100)}%`
}

// monthLabel shows "2026-09" as "September 2026".
export function monthLabel(month: string): string {
  const { year, month: monthIndex } = parseMonth(month)
  return `${MONTHS[monthIndex - 1]} ${year}`
}

function shiftMonth(month: string, delta: number): string {
  const { year, month: monthIndex } = parseMonth(month)
  const index = year * 12 + (monthIndex - 1) + delta
  const nextYear = Math.floor(index / 12)
  const nextMonthIndex = (index % 12) + 1
  return `${nextYear}-${String(nextMonthIndex).padStart(2, '0')}`
}

export function prevMonth(month: string): string {
  return shiftMonth(month, -1)
}

export function nextMonth(month: string): string {
  return shiftMonth(month, 1)
}

// taxYearFor finds the UK tax year (6 April - 5 April) a date falls in, named
// by its starting year: 2026 means 2026/27. Accepts "YYYY-MM" or
// "YYYY-MM-DD". Day-level: 1-5 April belong to the previous tax year, matching
// the store's earnings.TaxYearOf. Without a day, April is treated as its own
// tax year, since a bare month can't say which side of the 6th it's on.
export function taxYearFor(date: string): number {
  const { year, month, day } = parseDate(date)
  if (month > 4) return year
  if (month < 4) return year - 1
  return day !== undefined && day < 6 ? year - 1 : year
}

export function taxYearLabel(taxYear: number): string {
  return `${taxYear}/${String((taxYear + 1) % 100).padStart(2, '0')}`
}

// regionSaveErrorMessage explains a failed tax region save. Saving the
// region PATCHes /v1/business, which creates the business profile if it
// doesn't exist yet — and that create fails validation on a missing
// trading_name. That specific case gets a message pointing at the fix;
// everything else falls back to the generic save error.
export function regionSaveErrorMessage(error: unknown): string {
  if (error instanceof ValidationError && 'trading_name' in error.fields) {
    return 'Add your business name in Settings → Your business first.'
  }
  return saveErrorMessage(error)
}

// visibleServiceLines drops "Where it came from" rows with nothing in them,
// e.g. a service or "Packages" line that earned £0 this month.
export function visibleServiceLines(lines: EarningsLine[]): EarningsLine[] {
  return lines.filter((line) => line.amount_pence !== 0)
}
